import { CustomCard } from '@/components';
import Switch from '@/components/Switch';
import { findLanguage } from '@/helpers';
import { useUpdateLanguageSettingsMutation } from '@/redux/api/api.caller';
import { ILanguageList } from '@/types/language';
import { Button, Divider, Text } from '@shopify/polaris';
import { ReactNode, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DETECTION_LABELS } from '../languages.config';
import { handleMutationResult } from '../toastResult';
import DetectionMethodModal from './modals/DetectionMethodModal';
import SetDefaultModal from './modals/SetDefaultModal';

const SettingRow = ({ label, help, control }: { label: string; help: string; control: ReactNode }) => (
  <div className="d-flex align-center justify-between" style={{ gap: 16, padding: '8px 0' }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <Text as="span" variant="bodyMd" fontWeight="semibold">
        {label}
      </Text>
      <div>
        <Text as="span" variant="bodySm" tone="subdued">
          {help}
        </Text>
      </div>
    </div>
    <div style={{ flexShrink: 0 }}>{control}</div>
  </div>
);

const LanguageSettingsCard = ({ list }: { list: ILanguageList }) => {
  const dispatch = useDispatch();
  const [showDefault, setShowDefault] = useState(false);
  const [showDetection, setShowDetection] = useState(false);
  const [updateSettings] = useUpdateLanguageSettingsMutation();

  const defaultName = list.defaultLanguage ? findLanguage(list.defaultLanguage)?.label || list.defaultLanguage : '—';

  const onToggleSwitcher = () =>
    updateSettings({ showLanguageSwitcher: !list.showLanguageSwitcher }).then((res: any) => handleMutationResult(dispatch, res));

  return (
    <>
      <CustomCard title="Tracking page settings" noContentPaddingTop>
        <SettingRow
          label="Default language"
          help={`Text in your Tracking page configuration is treated as ${defaultName}. Used as fallback when detection doesn't match a published language.`}
          control={<Button onClick={() => setShowDefault(true)}>Change</Button>}
        />
        <Divider />
        <SettingRow
          label="Language detection method"
          help={`Currently using ${DETECTION_LABELS[list.detectionMethod]} — how each visitor's language is detected.`}
          control={<Button onClick={() => setShowDetection(true)}>Change</Button>}
        />
        <Divider />
        <SettingRow
          label="Show language switcher to visitors"
          help="Adds a language picker to the tracking page so visitors can change languages."
          control={<Switch isActive={list.showLanguageSwitcher} onClick={onToggleSwitcher} tooltip="language switcher" />}
        />
      </CustomCard>
      <SetDefaultModal open={showDefault} onClose={() => setShowDefault(false)} list={list} />
      <DetectionMethodModal open={showDetection} onClose={() => setShowDetection(false)} current={list.detectionMethod} />
    </>
  );
};

export default LanguageSettingsCard;
